import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSseSubscription } from '../context/SseContext';
import NavHeader from '../components/NavHeader';
import ChecklistProgressBar from '../components/ChecklistProgressBar';
import RequestorApproverModal from '../components/RequestorApproverModal';
import {
  fetchLuxDashboardProjection,
  generateLuxBatch,
  getLuxFxOptions,
  getLuxFxOptionsPreBatch,
  downloadLuxBatch,
  downloadLuxApRequest,
  regenerateLuxCategoryB,
  downloadLuxApRequestFile,
  submitLuxBatch,
  uploadLuxSignedDocuments,
  getLuxSignedDocuments,
  getLuxBatchChecklist,
  toggleLuxBatchChecklistItem,
  updateInvoiceFields,
} from '../api';

const ALL_QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const DEFAULT_REQUESTOR = 'Sandra Bantic - Senior Tax Analyst';

// The three Lux checklist labels (must match src/invoice/domain/lux_constants.py
// LUX_CHECKLIST_LABELS). Used to gate each checkbox in the UI.
const LUX_ITEM_CATEGORY_B_SENT = 'Category B form sent to Christophe Fender';
const LUX_ITEM_SIGNED_RECEIVED = 'Signed Category B received & uploaded';
const LUX_ITEM_BUYER_PACKAGE = 'Buyer request package generated';

// Compute whether a checklist item may be completed and, if not, why. Mirrors
// the server-side gating in LuxChecklistService._gate_reason_for_completion so
// a disabled checkbox tells the user what to do first. Unchecking a completed
// item is always allowed.
function checklistItemGate(item, batch, items, signedDocCount) {
  if (item.completed) return { disabled: false, reason: null };

  const byLabel = (label) => (items || []).find((it) => it.label === label);

  if (item.label === LUX_ITEM_CATEGORY_B_SENT) {
    const ready = !!(batch && batch.package_s3_key && batch.category_b_downloaded_at);
    return ready
      ? { disabled: false, reason: null }
      : { disabled: true, reason: 'Generate and download the Category B form first' };
  }

  if (item.label === LUX_ITEM_SIGNED_RECEIVED) {
    // Prefer the freshest signal: the locally fetched signed-doc list or the
    // projection's count, whichever is higher.
    const projCount = (batch && batch.signed_document_count) || 0;
    const signedCount = Math.max(projCount, signedDocCount || 0);
    return signedCount >= 1
      ? { disabled: false, reason: null }
      : { disabled: true, reason: 'Upload the signed Category B first' };
  }

  if (item.label === LUX_ITEM_BUYER_PACKAGE) {
    const item1 = byLabel(LUX_ITEM_CATEGORY_B_SENT);
    const item2 = byLabel(LUX_ITEM_SIGNED_RECEIVED);
    const ready = !!(item1 && item1.completed && item2 && item2.completed);
    return ready
      ? { disabled: false, reason: null }
      : { disabled: true, reason: 'Complete the first two steps first' };
  }

  return { disabled: false, reason: null };
}

function formatCurrency(amount, currency) {
  if (amount == null || amount === '') return '';
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
}

function LuxStatusBadge({ status }) {
  if (!status) return <span className="vat-status vat-status-open">Open</span>;
  const labels = { draft: 'Draft', packaged: 'Packaged', submitted: 'Submitted', paid: 'Paid' };
  return <span className={`vat-status vat-status-${status}`}>{labels[status] || status}</span>;
}

function LuxembourgDashboard() {
  const navigate = useNavigate();
  const fileInputRefs = useRef({});

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [lastHash, setLastHash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyQuarter, setBusyQuarter] = useState(null);
  const [toast, setToast] = useState(null);

  // Per-batch expand toggle for the inline 3-item checklist.
  const [expandedBatches, setExpandedBatches] = useState(() => new Set());
  const [batchChecklistItems, setBatchChecklistItems] = useState({});
  const [checklistBusyItem, setChecklistBusyItem] = useState(null);

  // Uploaded signed documents per batch (P0 item 4d). Loaded on demand.
  const [signedDocs, setSignedDocs] = useState({});
  // Per-invoice draft text for the editable "Invoice Text" (ai_summary) column,
  // keyed by invoice_id so editing one row never touches another.
  const [summaryDrafts, setSummaryDrafts] = useState({});
  const [summarySaving, setSummarySaving] = useState({});

  // Cover-sheet Requestor/Approver modal for the "Generate + Download AP
  // Request" action. Holds the target quarter while open; the names default to
  // the same VAT values (Requestor prefilled, Approver blank) and are sent to
  // the backend so they fill the AP request cover sheets, along with the chosen
  // FX rate. The batch is materialized on confirm before the AP zip is built.
  const [apRequestQuarter, setApRequestQuarter] = useState(null);
  const [apRequestBusy, setApRequestBusy] = useState(false);
  // FX-rate options for the AP request modal (fetched when it opens). Loaded
  // pre-batch from the eligible invoices so the picker shows before the batch
  // is materialized.
  const [apRequestFxOptions, setApRequestFxOptions] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadBatchChecklist = useCallback(async (batchId) => {
    try {
      const res = await getLuxBatchChecklist(batchId);
      setBatchChecklistItems((prev) => ({ ...prev, [batchId]: res.data || [] }));
    } catch {
      // Leave the items unset — the row will show a brief loading state.
    }
  }, []);

  const loadSignedDocs = useCallback(async (batchId) => {
    try {
      const res = await getLuxSignedDocuments(batchId);
      setSignedDocs((prev) => ({ ...prev, [batchId]: res.data || [] }));
    } catch {
      // Leave unset — the section simply shows nothing until it loads.
    }
  }, []);

  const toggleBatchExpanded = (batchId) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
        if (!batchChecklistItems[batchId]) {
          loadBatchChecklist(batchId);
        }
      }
      return next;
    });
  };

  const loadData = useCallback(async () => {
    try {
      const res = await fetchLuxDashboardProjection();
      const data = res.data;
      if (data.hash && data.hash === lastHash) return;
      setLastHash(data.hash);
      const fys = data.payload?.fiscal_years ?? [];
      setFiscalYears(fys);
      setSelectedFiscalYear((current) => {
        if (current != null && fys.some((fy) => fy.fiscal_year === current)) {
          return current;
        }
        return fys[0]?.fiscal_year ?? null;
      });
      // Pull the signed-document list for every materialized batch so the
      // "Signed documents" section stays in sync (P0 item 4d).
      for (const fy of fys) {
        for (const q of fy.quarters || []) {
          if (q.batch?.id) loadSignedDocs(q.batch.id);
        }
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load Luxembourg dashboard');
    } finally {
      setLoading(false);
    }
  }, [lastHash, loadSignedDocs]);

  const handleSaveSummary = useCallback(
    async (invoiceId, currentValue) => {
      const draft = summaryDrafts[invoiceId];
      const value = draft === undefined ? currentValue || '' : draft;
      setSummarySaving((prev) => ({ ...prev, [invoiceId]: true }));
      try {
        await updateInvoiceFields(invoiceId, { ai_summary: value });
        showToast('Invoice text saved');
        // Drop the local draft so the row reflects the freshly loaded value.
        setSummaryDrafts((prev) => {
          const next = { ...prev };
          delete next[invoiceId];
          return next;
        });
        loadData();
      } catch (err) {
        showToast(err.response?.data?.detail || 'Save failed', 'error');
      } finally {
        setSummarySaving((prev) => {
          const next = { ...prev };
          delete next[invoiceId];
          return next;
        });
      }
    },
    [summaryDrafts, showToast, loadData]
  );

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE — refresh via the shared connection (loadData early-returns on an
  // unchanged hash, so redundant messages are cheap).
  useSseSubscription(() => loadData());

  const selectedFyEntry = useMemo(
    () => fiscalYears.find((fy) => fy.fiscal_year === selectedFiscalYear) || null,
    [fiscalYears, selectedFiscalYear]
  );

  const quarters = useMemo(() => {
    if (!selectedFyEntry) return [];
    const byName = Object.fromEntries(
      (selectedFyEntry.quarters || []).map((q) => [q.quarter, q])
    );
    return ALL_QUARTERS.map((q) => byName[q]).filter(Boolean);
  }, [selectedFyEntry]);

  // Fiscal-year roll-up for the colored summary strip (mirrors the VAT page).
  const fyTotals = useMemo(() => {
    let total = 0;
    let invoices = 0;
    let packages = 0;
    let currency = '';
    for (const q of quarters) {
      total += parseFloat(q.total_amount || 0) || 0;
      for (const v of q.vendors || []) invoices += (v.invoices || []).length;
      if (q.batch) packages += 1;
      if (!currency && q.currency) currency = q.currency;
    }
    return { total, invoices, packages, currency };
  }, [quarters]);

  const withBusy = async (key, fn) => {
    setBusyQuarter(key);
    try {
      await fn();
    } finally {
      setBusyQuarter(null);
    }
  };

  // Generate + Download Category B — local-currency only, so NO modal and NO
  // FX. Materialize the batch on demand (idempotent find-or-create; pass no fx)
  // then download the Category B PDF and open it. This also stamps
  // category_b_downloaded_at, which gates the checklist.
  const handleGenerateAndDownloadCategoryB = (quarter) =>
    withBusy(`catb-${quarter}`, async () => {
      try {
        const gen = await generateLuxBatch(selectedFiscalYear, quarter);
        const batchId = gen.data?.id;
        if (!batchId) {
          showToast('Generate failed', 'error');
          return;
        }
        const res = await downloadLuxBatch(batchId);
        if (res.data?.url) window.open(res.data.url, '_blank');
        loadData();
      } catch (err) {
        showToast(err.response?.data?.detail || 'Generate failed', 'error');
      }
    });

  // Download the EXISTING Category B package — no rebuild. Stamps
  // category_b_downloaded_at on first download (gates the checklist).
  const handleDownloadCategoryB = (quarter, batchId) =>
    withBusy(`catb-${quarter}`, async () => {
      try {
        const res = await downloadLuxBatch(batchId);
        if (res.data?.url) window.open(res.data.url, '_blank');
        loadData();
      } catch (err) {
        showToast(err.response?.data?.detail || 'Download failed', 'error');
      }
    });

  // Regenerate Category B — force-rebuild the Category B package (local
  // currency, no modal) and open it. Does NOT touch the AP request zip.
  const handleRegenerateCategoryB = (quarter, batchId) =>
    withBusy(`catb-regen-${quarter}`, async () => {
      try {
        const res = await regenerateLuxCategoryB(batchId);
        if (res.data?.url) window.open(res.data.url, '_blank');
        loadData();
      } catch (err) {
        showToast(err.response?.data?.detail || 'Regenerate failed', 'error');
      }
    });

  // Download the EXISTING AP request zip — no rebuild, no modal.
  const handleDownloadApRequestFile = (quarter, batchId) =>
    withBusy(`ap-${quarter}`, async () => {
      try {
        const res = await downloadLuxApRequestFile(batchId);
        if (res.data?.url) window.open(res.data.url, '_blank');
      } catch (err) {
        showToast(err.response?.data?.detail || 'Download failed', 'error');
      }
    });

  // Open the cover-sheet Requestor/Approver modal for the AP request download.
  // Pulls the pre-batch FX-rate options (prospective currency + rates from the
  // eligible invoices) so the user can review/override the rate the AP request
  // package uses; the batch is materialized on confirm.
  const openApRequestModal = async (quarter, batchId) => {
    setApRequestFxOptions(null);
    setApRequestQuarter(quarter);
    try {
      // Once a batch exists its eligible invoices have left lux_open, so the
      // pre-batch lookup returns nothing — use the batch-scoped fx-options
      // (currency + available rates) whenever we have a batch id.
      const fx = batchId
        ? await getLuxFxOptions(batchId)
        : await getLuxFxOptionsPreBatch(selectedFiscalYear, quarter);
      setApRequestFxOptions(fx.data || null);
    } catch {
      // No picker if the rates can't be resolved — the download still works.
    }
  };

  const closeApRequestModal = () => {
    if (apRequestBusy) return;
    setApRequestQuarter(null);
    setApRequestFxOptions(null);
  };

  // Confirm — materialize the batch (no fx), then send the (optional)
  // Requestor/Approver names plus the chosen FX rate so they fill the AP request
  // cover sheets and drive the USD amounts, then open the returned zip URL.
  const handleConfirmApRequest = async (requestorName, approverName, fx) => {
    if (!apRequestQuarter) return;
    const quarter = apRequestQuarter;
    setApRequestBusy(true);
    try {
      const gen = await generateLuxBatch(selectedFiscalYear, quarter);
      const batchId = gen.data?.id;
      if (!batchId) {
        showToast('Generate failed', 'error');
        return;
      }
      const res = await downloadLuxApRequest(batchId, {
        requestor_name: (requestorName || '').trim() || null,
        approver_name: (approverName || '').trim() || null,
        fx_rate_override: fx ? fx.fx_rate : null,
        fx_rate_override_date: fx ? fx.fx_rate_date : null,
      });
      if (res.data?.url) window.open(res.data.url, '_blank');
      setApRequestQuarter(null);
      setApRequestFxOptions(null);
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Download failed', 'error');
    } finally {
      setApRequestBusy(false);
    }
  };

  const handleUploadSigned = (batchId, files) =>
    withBusy(`upload-${batchId}`, async () => {
      try {
        await uploadLuxSignedDocuments(batchId, files);
        showToast(
          files.length > 1
            ? `${files.length} signed documents uploaded`
            : 'Signed document uploaded'
        );
        loadData();
        loadSignedDocs(batchId);
      } catch (err) {
        showToast(err.response?.data?.detail || 'Upload failed', 'error');
      }
    });

  const handleSubmit = (batchId) =>
    withBusy(`submit-${batchId}`, async () => {
      try {
        await submitLuxBatch(batchId);
        showToast('Batch sent to AP');
        loadData();
      } catch (err) {
        showToast(err.response?.data?.detail || 'Submit failed', 'error');
      }
    });

  const handleToggleChecklistItem = async (batchId, item) => {
    setChecklistBusyItem(item.id);
    setBatchChecklistItems((prev) => {
      const list = prev[batchId] || [];
      return {
        ...prev,
        [batchId]: list.map((it) =>
          it.id === item.id ? { ...it, completed: !item.completed } : it
        ),
      };
    });
    try {
      await toggleLuxBatchChecklistItem(batchId, item.id, !item.completed);
      loadData();
      loadBatchChecklist(batchId);
    } catch (err) {
      setBatchChecklistItems((prev) => {
        const list = prev[batchId] || [];
        return {
          ...prev,
          [batchId]: list.map((it) =>
            it.id === item.id ? { ...it, completed: item.completed } : it
          ),
        };
      });
      showToast(err.response?.data?.detail || 'Failed to update checklist', 'error');
    } finally {
      setChecklistBusyItem(null);
    }
  };

  if (loading) {
    return (
      <div className="kanban-page">
        <NavHeader />
        <div className="board-loading">
          <div className="loading-spinner">Loading Luxembourg dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="kanban-page">
      <NavHeader />

      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      <div className="vat-controls">
        <div className="vat-controls-left">
          <label className="vat-control-label">Fiscal Year</label>
          <select
            className="status-select"
            value={selectedFiscalYear ?? ''}
            onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
            disabled={!fiscalYears.length}
          >
            {fiscalYears.map((fy) => (
              <option key={fy.fiscal_year} value={fy.fiscal_year}>
                FY {fy.fiscal_year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {quarters.length > 0 && (
        <div className="vat-todate-strip">
          <span className="vat-todate-scope">FY {selectedFiscalYear}</span>
          <span className="vat-todate-item">
            Total: <strong>{formatCurrency(fyTotals.total, fyTotals.currency)}</strong>
          </span>
          <span className="vat-todate-item">
            Invoices: <strong>{fyTotals.invoices}</strong>
          </span>
          <span className="vat-todate-item">
            Batches: <strong>{fyTotals.packages}</strong>
          </span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {!error && !quarters.length && (
        <div className="vat-empty">
          No Luxembourg invoices for FY {selectedFiscalYear ?? '—'}.
        </div>
      )}

      {quarters.map((q) => {
        const batch = q.batch;
        const hasOpenInvoices = (q.vendors || []).some((v) =>
          (v.invoices || []).some((i) => i.status === 'lux_open')
        );
        // Both downloads now generate-on-demand: available whenever there is
        // something to package — eligible open invoices, or an existing batch
        // not yet submitted (so a user can re-pull the docs before sending).
        const canPackage =
          hasOpenInvoices || (batch && ['draft', 'packaged'].includes(batch.status));
        const canSubmit = batch && batch.status === 'packaged' && batch.signed_approval_attached;
        const canUploadSigned = batch && ['draft', 'packaged'].includes(batch.status);
        // Per-artefact "already built" flags drive the green Download state +
        // the secondary Regenerate button (vs. the blue first-build button).
        const categoryBBuilt = !!(batch && batch.package_s3_key);
        const apRequestBuilt = !!(batch && batch.ap_request_package_s3_key);
        const uploadKey = `signed-${batch?.id || q.quarter}`;
        const batchSignedDocs = (batch && signedDocs[batch.id]) || [];
        const checklistProgress = batch?.checklist_progress;
        const checklistIncomplete =
          !checklistProgress || checklistProgress.items_completed < checklistProgress.items_total;
        const expanded = batch && expandedBatches.has(batch.id);

        return (
          <div className="vat-period" key={q.quarter}>
            <div className="vat-period-header">
              <h2 className="vat-period-title">
                {q.quarter} — FY {selectedFiscalYear}
                {q.quarter_start && q.quarter_end && (
                  <span className="vat-period-dates">
                    {' '}({q.quarter_start} to {q.quarter_end})
                  </span>
                )}
              </h2>
              <div className="vat-period-totals">
                <span className="vat-period-total-item">
                  Total: <strong className="num">{formatCurrency(q.total_amount, q.currency)}</strong>
                </span>
                <span className="vat-period-total-item">
                  <LuxStatusBadge status={batch ? batch.status : null} />
                </span>
                {batch && (
                  <span className="vat-period-total-item">
                    <button
                      type="button"
                      className="checklist-expand-toggle"
                      onClick={() => toggleBatchExpanded(batch.id)}
                      aria-expanded={!!expanded}
                      title={expanded ? 'Hide checklist' : 'Show checklist'}
                    >
                      {expanded ? '▾' : '▸'} Checklist
                    </button>
                    <ChecklistProgressBar progress={checklistProgress} />
                  </span>
                )}
              </div>
            </div>

            <div className="vat-period-actions">
              {/* Category B — drive off batch.package_s3_key */}
              {canPackage && !categoryBBuilt && (
                <button
                  className="btn btn-primary"
                  disabled={busyQuarter === `catb-${q.quarter}`}
                  onClick={() => handleGenerateAndDownloadCategoryB(q.quarter)}
                >
                  {busyQuarter === `catb-${q.quarter}`
                    ? 'Working…'
                    : 'Generate + Download Category B'}
                </button>
              )}
              {canPackage && categoryBBuilt && (
                <>
                  <button
                    className="btn btn-success"
                    disabled={busyQuarter === `catb-${q.quarter}`}
                    onClick={() => handleDownloadCategoryB(q.quarter, batch.id)}
                  >
                    {busyQuarter === `catb-${q.quarter}`
                      ? 'Working…'
                      : 'Download Category B'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: 8 }}
                    disabled={busyQuarter === `catb-regen-${q.quarter}`}
                    onClick={() => handleRegenerateCategoryB(q.quarter, batch.id)}
                  >
                    {busyQuarter === `catb-regen-${q.quarter}`
                      ? 'Working…'
                      : 'Regenerate'}
                  </button>
                </>
              )}

              {/* AP Request — drive off batch.ap_request_package_s3_key */}
              {canPackage && !apRequestBuilt && (
                <button
                  className="btn btn-primary"
                  style={{ marginLeft: 8 }}
                  disabled={apRequestBusy && apRequestQuarter === q.quarter}
                  onClick={() => openApRequestModal(q.quarter, batch?.id)}
                >
                  Generate + Download AP Request (zip)
                </button>
              )}
              {canPackage && apRequestBuilt && (
                <>
                  <button
                    className="btn btn-success"
                    style={{ marginLeft: 8 }}
                    disabled={busyQuarter === `ap-${q.quarter}`}
                    onClick={() => handleDownloadApRequestFile(q.quarter, batch.id)}
                  >
                    {busyQuarter === `ap-${q.quarter}`
                      ? 'Working…'
                      : 'Download AP Request (zip)'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: 8 }}
                    disabled={apRequestBusy && apRequestQuarter === q.quarter}
                    onClick={() => openApRequestModal(q.quarter, batch.id)}
                  >
                    Regenerate
                  </button>
                </>
              )}
              {canUploadSigned && (
                <>
                  <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    style={{ display: 'none' }}
                    ref={(el) => { fileInputRefs.current[uploadKey] = el; }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) handleUploadSigned(batch.id, files);
                      e.target.value = '';
                    }}
                  />
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    disabled={busyQuarter === `upload-${batch.id}`}
                    onClick={() => fileInputRefs.current[uploadKey]?.click()}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <path d="M17 8l-5-5-5 5" />
                      <path d="M12 3v12" />
                    </svg>
                    Upload Signed Documents
                  </button>
                </>
              )}
              {canSubmit && (
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: 8 }}
                  disabled={busyQuarter === `submit-${batch.id}` || checklistIncomplete}
                  onClick={() => handleSubmit(batch.id)}
                  title={
                    checklistIncomplete
                      ? 'Complete the checklist before sending to AP'
                      : 'Send this batch to AP'
                  }
                >
                  Send to AP
                </button>
              )}
            </div>

            {batch && batchSignedDocs.length > 0 && (
              <div className="lux-signed-docs">
                <div className="vat-checklist-panel-title">Signed documents</div>
                <ul className="lux-signed-docs-list">
                  {batchSignedDocs.map((doc) => (
                    <li key={doc.id || doc.document_id}>
                      <a
                        href={`/api/invoices/${doc.invoice_id}/documents/${doc.document_id}/preview`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.filename || 'Signed document'}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {batch && expanded && (
              <div className="vat-checklist-panel">
                <div className="vat-checklist-panel-title">
                  Submission package checklist
                </div>
                {batchChecklistItems[batch.id] ? (
                  <ul className="batch-checklist-list">
                    {batchChecklistItems[batch.id].map((item) => {
                      const gate = checklistItemGate(
                        item,
                        batch,
                        batchChecklistItems[batch.id],
                        batchSignedDocs.length
                      );
                      const disabled =
                        checklistBusyItem === item.id || gate.disabled;
                      return (
                        <li key={item.id}>
                          <label
                            className="batch-checklist-row"
                            title={gate.reason || undefined}
                          >
                            <input
                              type="checkbox"
                              checked={!!item.completed}
                              disabled={disabled}
                              onChange={() => handleToggleChecklistItem(batch.id, item)}
                            />
                            <span className="batch-checklist-label">{item.label}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="batch-checklist-loading">Loading checklist…</div>
                )}
              </div>
            )}

            <table className="vat-table">
              <colgroup>
                <col className="vat-col-vendor" />
                <col className="vat-col-invnum" />
                <col className="vat-col-date" />
                <col className="vat-col-amount" />
                <col className="vat-col-status" />
                <col className="vat-col-invtext" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Invoice #</th>
                  <th>Invoice Date</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Status</th>
                  <th>Invoice Text</th>
                </tr>
              </thead>
              <tbody>
                {(q.vendors || []).map((v) => (
                  <React.Fragment key={v.vendor_id || v.vendor_name}>
                    {(v.invoices || []).map((inv, idx) => (
                      <tr
                        key={inv.invoice_id}
                        onClick={() => navigate(`/invoice/${inv.invoice_id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {idx === 0 && (
                          <td
                            rowSpan={(v.invoices || []).length}
                            className="vat-vendor-cell"
                          >
                            <div className="vat-vendor-name">
                              {v.vendor_name || '—'}
                            </div>
                            <div className="vat-vendor-bank num">
                              {formatCurrency(v.subtotal_amount, v.currency)}
                            </div>
                          </td>
                        )}
                        <td className="code">{inv.invoice_number || '—'}</td>
                        <td className="num">{inv.invoice_date || '—'}</td>
                        <td className="num" style={{ textAlign: 'right' }}>
                          {formatCurrency(inv.amount, inv.currency)}
                        </td>
                        <td>
                          <LuxStatusBadge status={inv.status?.replace('lux_', '')} />
                        </td>
                        <td
                          className="vat-invtext-cell"
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: 'default' }}
                        >
                          <textarea
                            className="vat-invtext-input"
                            maxLength={500}
                            rows={2}
                            value={
                              summaryDrafts[inv.invoice_id] !== undefined
                                ? summaryDrafts[inv.invoice_id]
                                : inv.ai_summary || ''
                            }
                            onChange={(e) =>
                              setSummaryDrafts((prev) => ({
                                ...prev,
                                [inv.invoice_id]: e.target.value,
                              }))
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={!!summarySaving[inv.invoice_id]}
                            onClick={() =>
                              handleSaveSummary(inv.invoice_id, inv.ai_summary)
                            }
                          >
                            {summarySaving[inv.invoice_id] ? 'Saving…' : 'Save'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      <RequestorApproverModal
        open={!!apRequestQuarter}
        title="Generate + Download AP Request"
        confirmLabel="Generate + Download AP Request"
        defaultRequestor={DEFAULT_REQUESTOR}
        fxOptions={apRequestFxOptions}
        busy={apRequestBusy}
        onConfirm={handleConfirmApRequest}
        onClose={closeApRequestModal}
      />
    </div>
  );
}

export default LuxembourgDashboard;
